import React, { useState, useEffect, useCallback } from 'react';
import { isEmbroideryTrace } from "../../config/appVariant";
import { IonIcon, IonSpinner } from '@ionic/react';
import {
  arrowBackOutline,
  cardOutline,
  sparklesOutline,
  openOutline,
} from 'ionicons/icons';
import { useAuth } from '../../contexts/AuthContext';
import { createPortalLinkFunc } from '../../lib/firebase/firebase';
import { getSubscriptionDetails } from '../../lib/stripe/getSubscriptionDetails';
import { isNativeiOS } from '../../lib/utils/platform';
import { Capacitor } from '@capacitor/core';
import CancelFlowModal from '../CancelFlowModal/CancelFlowModal';
import './SettingsPage.css';

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatPrice = (priceData) => {
  if (!priceData) return null;
  const amount = priceData.unit_amount;
  if (amount == null) return null;
  return `$${(amount / 100).toFixed(2)}`;
};

const formatInterval = (priceData) => {
  if (!priceData?.recurring?.interval) return '';
  const interval = priceData.recurring.interval;
  if (interval === 'month') return '/month';
  if (interval === 'year') return '/year';
  return `/${interval}`;
};

const SubscriptionSettingsPage = ({ onBack }) => {
  const { user, userIsPremium } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCancelFlow, setShowCancelFlow] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError('');
    try {
      const details = await getSubscriptionDetails(user.uid);
      setSubscription(details);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Could not load subscription details.');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await createPortalLinkFunc({
        returnUrl: window.location.origin + '/home',
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Portal link error:', err);
      setError('Could not open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleManageAppStore = () => {
    if (Capacitor.isNativePlatform()) {
      window.open('https://apps.apple.com/account/subscriptions', '_system');
    } else {
      window.open('https://apps.apple.com/account/subscriptions', '_blank');
    }
  };

  const isPro = !!userIsPremium;
  const isLifetime = userIsPremium === 'lifetime-pro';
  const isRevenueCatSub = subscription?.subscriptionSource === 'revenuecat';

  const priceStr = subscription ? formatPrice(subscription.price) : null;
  const interval = subscription ? formatInterval(subscription.price) : '';
  const planName = subscription?.product?.name || (interval === '/year' ? 'Yearly' : interval === '/month' ? 'Monthly' : 'Pro');

  return (
    <div className="settings-page">
      <div className="sp-header">
        <button className="sp-back-btn" onClick={onBack}>
          <IonIcon icon={arrowBackOutline} />
        </button>
        <h2 className="sp-header-title">Subscription</h2>
      </div>

      <div className="sp-scroll">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <IonSpinner name="lines" style={{ color: '#667eea' }} />
          </div>
        ) : (
          <>
            {/* Current Plan */}
            <div className="sp-section">
              <p className="sp-section-title">Current Plan</p>
              <div className="sp-item sp-item--static">
                <div
                  className="sp-item-icon"
                  style={{
                    background: isPro ? 'linear-gradient(135deg, #f453c4 0%, #667eea 100%)' : '#f1f5f9',
                    color: isPro ? '#fff' : '#94a3b8',
                  }}
                >
                  <IonIcon icon={isPro ? sparklesOutline : cardOutline} />
                </div>
                <div className="sp-item-body">
                  <p className="sp-item-title">
                    {isLifetime ? 'Lifetime Pro' : isPro ? `SvgTrace — ${planName}` : 'Free Plan'}
                  </p>
                  <p className="sp-item-sub">
                    {isLifetime
                      ? 'One-time purchase — never expires'
                      : isPro
                      ? subscription?.cancelAtPeriodEnd
                        ? `Canceling — access until ${formatDate(subscription.currentPeriodEnd)}`
                        : subscription?.status === 'trialing'
                        ? `Free trial — converts ${formatDate(subscription.trialEnd)}`
                        : priceStr
                        ? `${priceStr}${interval}`
                        : 'Active subscription'
                      : 'Basic conversion features'}
                  </p>
                </div>
              </div>

              {isPro && subscription?.currentPeriodEnd && !isLifetime && (
                <div className="sp-item sp-item--static">
                  <div className="sp-item-icon" style={{ background: '#f1f5f9', color: '#64748b' }}>
                    <IonIcon icon={cardOutline} />
                  </div>
                  <div className="sp-item-body">
                    <p className="sp-item-sub" style={{ margin: 0 }}>
                      {subscription.cancelAtPeriodEnd ? 'Access until' : 'Renews on'}
                    </p>
                    <p className="sp-item-title">
                      {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Manage Subscription */}
            {isPro && (
              <div className="sp-section">
                <p className="sp-section-title">Manage</p>
                {isLifetime ? (
                  <div className="sp-info-box sp-info-box--info" style={{ margin: 18 }}>
                    <IonIcon icon={sparklesOutline} className="sp-info-box-icon" style={{ color: '#3b82f6' }} />
                    <p className="sp-info-box-text">
                      You have lifetime access to all SvgTrace features. No further payments needed.
                    </p>
                  </div>
                ) : isRevenueCatSub ? (
                  <div style={{ padding: '8px 18px 18px' }}>
                    <div className="sp-info-box sp-info-box--info" style={{ margin: '0 0 14px' }}>
                      <p className="sp-info-box-text">
                        Your subscription was purchased through the App Store. To change your plan, update payment, or cancel, use your Apple ID settings.
                      </p>
                    </div>
                    <button className="sp-btn-primary" onClick={handleManageAppStore}>
                      <IonIcon icon={openOutline} style={{ fontSize: 16 }} />
                      Manage in App Store
                    </button>
                    <button
                      onClick={() => setShowCancelFlow(true)}
                      style={{
                        width: '100%',
                        marginTop: 12,
                        padding: '10px 16px',
                        background: 'none',
                        border: '1.5px solid #fecaca',
                        borderRadius: 10,
                        color: '#dc2626',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Cancel Subscription
                    </button>
                    {!isNativeiOS() && (
                      <p style={{ fontSize: 12, color: '#f59e0b', marginTop: 10, lineHeight: 1.4 }}>
                        You subscribed on iOS. Billing changes must be made from your iPhone in Settings → Apple ID → Subscriptions.
                      </p>
                    )}
                  </div>
                ) : isNativeiOS() && !isRevenueCatSub ? (
                  <div style={{ padding: '8px 18px 18px' }}>
                    <div className="sp-info-box sp-info-box--info" style={{ margin: '0 0 14px' }}>
                      <p className="sp-info-box-text">
                        Your subscription was purchased on the web. To manage, change, or cancel it, visit svgtrace.com and go to your subscription settings.
                      </p>
                    </div>
                    <p style={{ fontSize: 12, color: '#f59e0b', marginTop: 0, lineHeight: 1.4 }}>
                      Billing changes cannot be made from the iOS app.
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '8px 18px 18px' }}>
                    <button
                      className="sp-btn-primary"
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                    >
                      {portalLoading ? (
                        <>
                          <IonSpinner name="lines" style={{ width: 16, height: 16 }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <IonIcon icon={openOutline} style={{ fontSize: 16 }} />
                          Manage Billing
                        </>
                      )}
                    </button>
                    <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
                      Update payment method or view invoices.
                    </p>
                    {!subscription?.cancelAtPeriodEnd && (
                      <button
                        onClick={() => setShowCancelFlow(true)}
                        style={{
                          width: '100%',
                          marginTop: 12,
                          padding: '10px 16px',
                          background: 'none',
                          border: '1.5px solid #fecaca',
                          borderRadius: 10,
                          color: '#dc2626',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Upgrade CTA for free users */}
            {!isPro && (
              <div
                className="sp-section"
                style={{
                  background: 'linear-gradient(135deg, #f453c4 0%, #667eea 100%)',
                  border: 'none',
                  color: '#fff',
                  padding: 20,
                  textAlign: 'center',
                }}
              >
                <IonIcon icon={sparklesOutline} style={{ fontSize: 32, marginBottom: 8 }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: '#fff' }}>
                  Upgrade to SvgTrace
                </h3>
                <p style={{ fontSize: 14, opacity: 0.9, margin: '0 0 16px', lineHeight: 1.5, color: '#fff' }}>
                  {isEmbroideryTrace ? "Get access to embroidery editing, stitch previews, and machine-file exports." : "Get unlimited access to AI Generate, Retrace, Modify, Resize, Bulk Convert, and all premium features."}
                </p>
                <button
                  className="sp-btn-secondary"
                  style={{ background: '#fff', color: '#667eea', borderColor: 'transparent', fontWeight: 700 }}
                  onClick={() => {
                    window.location.href = '/checkout';
                  }}
                >
                  View Plans
                </button>
              </div>
            )}

            {error && (
              <div className="sp-info-box sp-info-box--danger" style={{ margin: '0 0 16px' }}>
                <p className="sp-info-box-text" style={{ color: '#ef4444' }}>{error}</p>
              </div>
            )}
          </>
        )}
      </div>

      <CancelFlowModal
        isOpen={showCancelFlow}
        onClose={() => {
          setShowCancelFlow(false);
          fetchSubscription();
        }}
        onCanceled={() => fetchSubscription()}
        isIOSSub={isRevenueCatSub}
      />
    </div>
  );
};

export default SubscriptionSettingsPage;
